import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
} from "../../../components/Component";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { getPublicUrl } from "../../../utils/Utils";
import { IntegrationsSettingsSection } from "./IntegrationsPage";
import { TeleconsultationTab, PlatformTab, QuickLinksTab } from "./PlatformSetupPage";
import { SystemSettingsMarketingPanel } from "./SystemSettings";
import { BillingLegalSettingsPanel } from "./BillingLegalSettingsPanel";
import EmailDeliverySettingsPanel from "./components/EmailDeliverySettingsPanel";

const TAB_IDS = ["integrations", "email", "teleconsultation", "platform", "billing", "marketing", "checklist"];

const SECTIONS = [
  { id: "integrations", label: "Integrations" },
  { id: "email", label: "Email delivery" },
  { id: "teleconsultation", label: "Teleconsultation" },
  { id: "platform", label: "Platform" },
  { id: "billing", label: "Billing & invoices" },
  { id: "marketing", label: "Marketing & security" },
  { id: "checklist", label: "Setup checklist" },
];

function readTabFromSearch(search) {
  const params = new URLSearchParams(search || "");
  const raw = (params.get("tab") || "").toLowerCase().trim();
  if (TAB_IDS.includes(raw)) return raw;
  return "integrations";
}

/**
 * Single admin entry point: business areas as tabs. Legacy URLs redirect here with ?tab=.
 */
const AdminSettingsHubPage = () => {
  const history = useHistory();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => readTabFromSearch(location.search));

  useEffect(() => {
    setActiveTab(readTabFromSearch(location.search));
  }, [location.search]);

  const setTab = useCallback(
    (id) => {
      setActiveTab(id);
      const params = new URLSearchParams(location.search);
      params.set("tab", id);
      history.replace({ pathname: location.pathname, search: params.toString() });
    },
    [history, location.pathname, location.search]
  );

  const base = useMemo(() => getPublicUrl("admin-settings"), []);

  return (
    <>
      <Head title="Settings" />
      <Content>
        <BlockHead size="lg">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h4">Settings</BlockTitle>
              <BlockDes>
                <p>
                  Configure the platform by business area: integrations, outbound email (Gmail + sender), teleconsultation rules,
                  partner-facing options, marketing/analytics, billing PDF copy, and go-live checklist.
                </p>
              </BlockDes>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Nav tabs className="mb-4 flex-wrap">
            {SECTIONS.map((s) => (
              <NavItem key={s.id}>
                <NavLink
                  href="#tab"
                  className={activeTab === s.id ? "active" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    setTab(s.id);
                  }}
                >
                  {s.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="integrations">
              {activeTab === "integrations" && <IntegrationsSettingsSection settingsBasePath={base} />}
            </TabPane>
            <TabPane tabId="email">
              {activeTab === "email" && <EmailDeliverySettingsPanel />}
            </TabPane>
            <TabPane tabId="teleconsultation">
              {activeTab === "teleconsultation" && <TeleconsultationTab />}
            </TabPane>
            <TabPane tabId="platform">
              {activeTab === "platform" && <PlatformTab />}
            </TabPane>
            <TabPane tabId="billing">
              {activeTab === "billing" && <BillingLegalSettingsPanel />}
            </TabPane>
            <TabPane tabId="marketing">
              {activeTab === "marketing" && <SystemSettingsMarketingPanel />}
            </TabPane>
            <TabPane tabId="checklist">
              {activeTab === "checklist" && <QuickLinksTab adminSettingsPath={base} />}
            </TabPane>
          </TabContent>
        </Block>
      </Content>
    </>
  );
};

export default AdminSettingsHubPage;
